import {
  Smartphone,
  Server,
  CircleUser,
  Network,
  Gauge,
  SquareActivity,
  Filter,
  Wand2,
  ShieldCheck,
  Bot,
  History,
  Globe,
  Type,
  StickyNote,
  Layers,
  SquarePen
} from "lucide-react";

// Export Layers icon for direct reference
// This ensures the component is available for direct import elsewhere
export { Layers } from "lucide-react";
import { MetaRouterIcon } from "@/config/icons";

// Theme colors for node types
export const nodeColors: Record<string, { theme: string, bg: string, text: string, bgDark: string, textDark: string }> = {
  "event": { theme: "orange", bg: "bg-orange-100", text: "text-orange-600", bgDark: "dark:bg-orange-500/10", textDark: "dark:text-orange-400" },
  "pipeline": { theme: "amber", bg: "bg-amber-100", text: "text-amber-600", bgDark: "dark:bg-amber-500/10", textDark: "dark:text-amber-400" },
  "transformations": { theme: "yellow", bg: "bg-yellow-100", text: "text-yellow-600", bgDark: "dark:bg-yellow-500/10", textDark: "dark:text-yellow-400" },
  "filter": { theme: "lime", bg: "bg-lime-100", text: "text-lime-600", bgDark: "dark:bg-lime-500/10", textDark: "dark:text-lime-400" },
  "schema-enforcement": { theme: "green", bg: "bg-green-100", text: "text-green-600", bgDark: "dark:bg-green-500/10", textDark: "dark:text-green-400" },
  "bot-filtering": { theme: "emerald", bg: "bg-emerald-100", text: "text-emerald-600", bgDark: "dark:bg-emerald-500/10", textDark: "dark:text-emerald-400" },
  "data-replay": { theme: "teal", bg: "bg-teal-100", text: "text-teal-600", bgDark: "dark:bg-teal-500/10", textDark: "dark:text-teal-400" },
  // Layout node colors
  "text-node": { theme: "gray", bg: "bg-gray-100", text: "text-gray-600", bgDark: "dark:bg-gray-500/10", textDark: "dark:text-gray-400" },
  "notes-node": { theme: "gray", bg: "bg-gray-100", text: "text-gray-600", bgDark: "dark:bg-gray-500/10", textDark: "dark:text-gray-400" },
   "group": { theme: "gray", bg: "bg-gray-100", text: "text-gray-600", bgDark: "dark:bg-gray-500/10", textDark: "dark:text-gray-400" },
   "custom": { theme: "gray", bg: "bg-gray-100", text: "text-gray-600", bgDark: "dark:bg-gray-500/10", textDark: "dark:text-gray-400" },
  // Default colors for other node types
  "source": { theme: "blue", bg: "bg-blue-100", text: "text-blue-600", bgDark: "dark:bg-blue-500/20", textDark: "dark:text-blue-400" },
  "cluster": { theme: "indigo", bg: "bg-indigo-100", text: "text-indigo-600", bgDark: "dark:bg-indigo-500/20", textDark: "dark:text-indigo-300" },
  "sync-injector": { theme: "indigo", bg: "bg-indigo-100", text: "text-indigo-600", bgDark: "dark:bg-indigo-500/20", textDark: "dark:text-indigo-400" },
  "load-balancer": { theme: "indigo", bg: "bg-indigo-100", text: "text-indigo-600", bgDark: "dark:bg-indigo-500/20", textDark: "dark:text-indigo-400" },
  "destination": { theme: "gray", bg: "bg-gray-50", text: "text-gray-600", bgDark: "dark:bg-gray-500/10", textDark: "dark:text-gray-400" }
};

export const staticItems = {
  sources: [
    { id: "source", name: "Website", icon: Globe, type: "javascript" },
    { id: "source", name: "Mobile SDK", icon: Smartphone, type: "mobile-sdk" },
    { id: "source", name: "Server/other", icon: Server, type: "server" },
  ],
  modules: [
    { id: "cluster", name: "MetaRouter Cluster", icon: MetaRouterIcon, type: "cluster" },
    {
      id: "sync-injector",
      name: "Sync Injector",
      icon: CircleUser,
      type: "sync-injector",
    },
    {
      id: "load-balancer",
      name: "Load Balancer",
      icon: Gauge,
      type: "load-balancer",
    },
  ],
  layout: [
    { id: "text-node", name: "Text", icon: Type, type: "text-node" },
    { id: "notes-node", name: "Notes", icon: StickyNote, type: "notes-node" },
    { id: "group", name: "Group", icon: Layers, type: "group" },
    { id: "custom", name: "Custom", icon: SquarePen, type: "custom" },
  ],
  pipeline: [
    { id: "event", name: "Event", icon: SquareActivity, type: "event" },
    { id: "pipeline", name: "Pipeline", icon: Network, type: "pipeline" },
    { id: "transformations", name: "Transformations", icon: Wand2, type: "transformations" },
    { id: "filter", name: "Filter", icon: Filter, type: "filter" },
    { id: "schema-enforcement", name: "Schema Enforcement", icon: ShieldCheck, type: "schema-enforcement" },
    { id: "bot-filtering", name: "Bot Filtering", icon: Bot, type: "bot-filtering" },
    { id: "data-replay", name: "Data Replay", icon: History, type: "data-replay" },
  ]
};

// Map node types to their specific node types in the application
export const nodeTypeMapping: Record<string, string> = {
  "source": "javascript-source",
  "cluster": "cluster",
  "sync-injector": "sync-injector",
  "load-balancer": "load-balancer",
  "transformations": "transformations",
  "schema-enforcement": "schema-enforcement",
  "bot-filtering": "bot-filtering",
  "data-replay": "data-replay",
  "filter": "filter",
  "event": "event",
  "pipeline": "pipeline",
  "destination": "destination",
  "text-node": "text-node",
  "notes-node": "notes-node",
  "group": "group",
  "custom": "custom", // Added custom node mapping
  // Keeping deprecated mappings for backward compatibility
};
